import { useState } from "react";
import { GoogleOAuthProvider, GoogleLogin } from "@react-oauth/google";

const GOOGLE_CLIENT_ID = "237390246632-95l45hruejdlvc8ddc6r36v5mcjulois.apps.googleusercontent.com";

function LoginPage({ onLogin }) {
  const [error, setError] = useState(null);

  const handleSuccess = async (credentialResponse) => {
    try {
      const res = await fetch("https://prompt-cache-production.up.railway.app/api/auth/google", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: credentialResponse.credential }),
      });
      const data = await res.json();
      if (data.access_token) {
        onLogin(data.access_token, data.user);
      } else {
        setError("Login failed. Please try again.");
      }
    } catch (err) {
      setError("Could not connect to server.");
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.loginBox}>
        <h1 style={styles.title}>Prompt Cache</h1>
        <p style={styles.subtitle}>
          Never hit a token limit wall again. Search millions of AI answers instantly.
        </p>
        <div style={styles.divider} />
        <p style={styles.loginLabel}>Sign in to continue</p>
        <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
          <GoogleLogin
            onSuccess={handleSuccess}
            onError={() => setError("Google login failed")}
            useOneTap
          />
        </GoogleOAuthProvider>
        {error && <p style={styles.error}>{error}</p>}
      </div>
    </div>
  );
}

function SearchPage({ user, token, onLogout }) {
  const [prompt, setPrompt] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSearch = async () => {
    if (!prompt.trim()) return;
    setLoading(true);
    setError(null);
    setResults(null);

    try {
      const res = await fetch("https://prompt-cache-production.up.railway.app/api/search", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ prompt }),
      });
      const data = await res.json();
      setResults(data);
    } catch (err) {
      setError("Could not connect to the API.");
    } finally {
      setLoading(false);
    }
  };

 const handleStore = async () => {
  if (!prompt.trim()) {
    alert("Please type a prompt first.");
    return;
  }
  const answer = window.prompt("Paste the AI answer you want to store (min 20 characters):");
  if (!answer || answer.trim().length < 20) {
    alert("Answer is too short. Please paste a complete answer.");
    return;
  }

  try {
    const res = await fetch("https://prompt-cache-production.up.railway.app/api/store", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        prompt: prompt.trim(),
        answer: answer.trim(),
        model_used: "user-contributed"
      }),
    });
    const data = await res.json();
    if (data.id) {
      alert("Prompt stored successfully!");
    } else {
      alert("Failed: " + JSON.stringify(data.detail || data));
    }
  } catch (err) {
    alert("Could not connect to server.");
  }
};

  const getScoreColor = (score) => {
    if (score >= 85) return "#22c55e";
    if (score >= 70) return "#f59e0b";
    return "#ef4444";
  };

  return (
    <div style={styles.page}>
      <div style={styles.container}>

        {/* Header */}
        <div style={styles.header}>
          <div style={styles.headerLeft}>
            <h1 style={styles.title}>Prompt Cache</h1>
          </div>
          <div style={styles.headerRight}>
            {user.picture && (
              <img src={user.picture} alt="avatar" style={styles.avatar} />
            )}
            <span style={styles.userName}>{user.name}</span>
            <button style={styles.logoutBtn} onClick={onLogout}>
              Logout
            </button>
          </div>
        </div>

        {/* Search Box */}
        <div style={styles.searchBox}>
          <textarea
            style={styles.textarea}
            placeholder="Type your prompt here..."
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            rows={3}
          />
          <div style={styles.buttonRow}>
            <button
              style={{ ...styles.button, opacity: loading ? 0.7 : 1 }}
              onClick={handleSearch}
              disabled={loading}
            >
              {loading ? "Searching..." : "Search"}
            </button>
            <button style={styles.storeBtn} onClick={handleStore}>
              + Store Answer
            </button>
          </div>
        </div>

        {error && <div style={styles.errorBox}>{error}</div>}

        {/* No Results */}
        {results && !results.found && (
          <div style={styles.noMatch}>
            <p style={{ fontSize: 32 }}>🤷</p>
            <p style={{ fontWeight: 600, fontSize: 16 }}>No similar prompt found</p>
            <p style={{ color: "#6b7280", fontSize: 14 }}>
              Nobody has asked something close to this yet. Store your answer to help others!
            </p>
          </div>
        )}

        {/* Results */}
        {results && results.found && (
          <div>
            <p style={styles.resultsLabel}>{results.matches.length} match(es) found</p>
            {results.matches.map((match, i) => (
              <div key={i} style={styles.card}>
                <div style={styles.cardHeader}>
                  <span style={styles.matchLabel}>Matched Prompt</span>
                  <span style={{
                    ...styles.scoreBadge,
                    backgroundColor: getScoreColor(match.similarity)
                  }}>
                    {match.similarity}% match
                  </span>
                </div>
                <p style={styles.matchedPrompt}>"{match.matched_prompt}"</p>
                <div style={styles.divider} />
                <p style={styles.answerLabel}>Answer</p>
                <p style={styles.answerText}>{match.answer}</p>
                <div style={styles.cardFooter}>
                  <span style={styles.tag}>Model: {match.model_used}</span>
                  <span style={styles.tag}>Upvotes: {match.upvotes}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
export default function App() {
  const [token, setToken] = useState(() => {
    return localStorage.getItem("pc_token") || null;
  });
  const [user, setUser] = useState(() => {
    const saved = localStorage.getItem("pc_user");
    return saved ? JSON.parse(saved) : null;
  });

  const handleLogin = (accessToken, userData) => {
    localStorage.setItem("pc_token", accessToken);
    localStorage.setItem("pc_user", JSON.stringify(userData));
    setToken(accessToken);
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem("pc_token");
    localStorage.removeItem("pc_user");
    setToken(null);
    setUser(null);
  };

  if (!token) {
    return <LoginPage onLogin={handleLogin} />;
  }

  return <SearchPage user={user} token={token} onLogout={handleLogout} />;
}
const styles = {
  page: {
    minHeight: "100vh",
    backgroundColor: "#f1f5f9",
    padding: "40px 16px",
    fontFamily: "'Segoe UI', sans-serif",
  },
  loginBox: {
    maxWidth: 420,
    margin: "80px auto",
    backgroundColor: "#ffffff",
    borderRadius: 16,
    padding: 40,
    boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
    textAlign: "center",
  },
  container: { maxWidth: 720, margin: "0 auto" },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 24,
  },
  headerLeft: {},
  headerRight: {
    display: "flex",
    alignItems: "center",
    gap: 10,
  },
  avatar: {
    width: 36,
    height: 36,
    borderRadius: "50%",
  },
  userName: {
    fontSize: 14,
    color: "#374151",
    fontWeight: 500,
  },
  logoutBtn: {
    fontSize: 13,
    padding: "6px 14px",
    backgroundColor: "#f1f5f9",
    border: "1px solid #e2e8f0",
    borderRadius: 8,
    cursor: "pointer",
    color: "#374151",
  },
  title: {
    fontSize: 32,
    fontWeight: 800,
    color: "#1e3a5f",
    margin: 0,
  },
  subtitle: {
    color: "#6b7280",
    fontSize: 14,
    marginTop: 8,
    lineHeight: 1.6,
  },
  loginLabel: {
    color: "#6b7280",
    fontSize: 14,
    marginBottom: 16,
  },
  divider: {
    height: 1,
    backgroundColor: "#f1f5f9",
    margin: "20px 0",
  },
  searchBox: {
    backgroundColor: "#ffffff",
    borderRadius: 12,
    padding: 20,
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
    marginBottom: 24,
  },
  textarea: {
    width: "100%",
    border: "1px solid #e2e8f0",
    borderRadius: 8,
    padding: "12px 14px",
    fontSize: 15,
    resize: "vertical",
    outline: "none",
    boxSizing: "border-box",
    fontFamily: "inherit",
  },
  buttonRow: {
    display: "flex",
    gap: 10,
    marginTop: 12,
  },
  button: {
    flex: 1,
    padding: "12px 0",
    backgroundColor: "#1e3a5f",
    color: "#ffffff",
    border: "none",
    borderRadius: 8,
    fontSize: 15,
    fontWeight: 600,
    cursor: "pointer",
  },
  storeBtn: {
    flex: 1,
    padding: "12px 0",
    backgroundColor: "#ffffff",
    color: "#1e3a5f",
    border: "2px solid #1e3a5f",
    borderRadius: 8,
    fontSize: 15,
    fontWeight: 600,
    cursor: "pointer",
  },
  errorBox: {
    backgroundColor: "#fee2e2",
    color: "#b91c1c",
    padding: 16,
    borderRadius: 8,
    marginBottom: 16,
    fontSize: 14,
  },
  noMatch: {
    backgroundColor: "#ffffff",
    borderRadius: 12,
    padding: 40,
    textAlign: "center",
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
  },
  resultsLabel: {
    color: "#6b7280",
    fontSize: 13,
    marginBottom: 12,
    fontWeight: 500,
  },
  card: {
    backgroundColor: "#ffffff",
    borderRadius: 12,
    padding: 20,
    marginBottom: 16,
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
  },
  cardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 8,
  },
  matchLabel: {
    fontSize: 12,
    fontWeight: 600,
    color: "#6b7280",
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  scoreBadge: {
    color: "#ffffff",
    fontSize: 12,
    fontWeight: 700,
    padding: "4px 10px",
    borderRadius: 20,
  },
  matchedPrompt: {
    fontSize: 15,
    color: "#1e3a5f",
    fontWeight: 600,
    margin: "0 0 12px 0",
  },
  answerLabel: {
    fontSize: 12,
    fontWeight: 600,
    color: "#6b7280",
    textTransform: "uppercase",
    letterSpacing: 0.5,
    marginBottom: 6,
  },
  answerText: {
    fontSize: 15,
    color: "#374151",
    lineHeight: 1.6,
    margin: 0,
  },
  cardFooter: {
    display: "flex",
    gap: 8,
    marginTop: 14,
  },
  tag: {
    fontSize: 12,
    backgroundColor: "#f1f5f9",
    color: "#6b7280",
    padding: "4px 10px",
    borderRadius: 20,
  },
  error: {
    color: "#b91c1c",
    fontSize: 13,
    marginTop: 12,
  },
};